import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { authErrorMessage, useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink, useGo } from '../components/Nav';
import { GoogleIcon } from '../components/icons';
import { Banner, Button, Input, Spinner } from '../components/ui';
import { firebaseConfigured } from '../lib/env';

/**
 * /login and /signup. Same frame, different verbs.
 *
 * Signup is open — individuals are free forever and org members join through
 * the same door. There is no "request a demo" step and there should never be
 * one.
 */

function AuthFrame({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="flex min-h-full flex-col bg-slate-950">
      <header className="border-b border-hairline">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center px-4 sm:px-6">
          <Wordmark />
        </div>
        <ClapperRule />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <h1 className="text-h2 text-chalk">{title}</h1>
          <p className="mt-1.5 text-body text-chalk-dim">{subtitle}</p>

          {!firebaseConfigured ? (
            <div className="mt-5">
              <Banner tone="danger" title="Sign-in is not configured">
                This build has no Firebase credentials. Run <code>yarn ssm:fetch:local</code> (or
                the environment you meant) before starting the dev server.
              </Banner>
            </div>
          ) : null}

          <div className="mt-6 space-y-5">{children}</div>

          <div className="mt-6 text-caption text-chalk-faint">{footer}</div>
        </div>
      </main>
    </div>
  );
}

function GoogleButton({ label, onError }: { label: string; onError: (message: string) => void }) {
  const { signInWithGoogle } = useAuth();
  const [busy, setBusy] = useState(false);

  return (
    <Button
      variant="secondary"
      size="lg"
      className="w-full"
      busy={busy}
      disabled={!firebaseConfigured}
      onClick={() => {
        setBusy(true);
        signInWithGoogle()
          .catch((error: unknown) => onError(authErrorMessage(error)))
          .finally(() => setBusy(false));
      }}
    >
      {!busy ? <GoogleIcon size={16} /> : null}
      {label}
    </Button>
  );
}

function Divider() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-hairline" />
      <span className="text-micro uppercase tracking-[0.1em] text-chalk-faint">or</span>
      <div className="h-px flex-1 bg-hairline" />
    </div>
  );
}

/** Both pages bounce to the library the moment a session exists. */
function useRedirectWhenSignedIn() {
  const { user, loading } = useAuth();
  const go = useGo();

  useEffect(() => {
    if (!loading && user) go('/', { replace: true });
  }, [go, loading, user]);

  return loading;
}

export function LoginPage() {
  const { signInWithEmail, sendReset } = useAuth();
  const restoring = useRedirectWhenSignedIn();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    signInWithEmail(email, password)
      .catch((cause: unknown) => setError(authErrorMessage(cause)))
      .finally(() => setBusy(false));
  };

  const reset = () => {
    if (!email.trim()) {
      setError('Enter your email address first, then ask for a reset link.');
      return;
    }
    setError(null);
    sendReset(email)
      .then(() => setNotice(`If there is an account for ${email.trim()}, a reset link is on its way.`))
      .catch((cause: unknown) => setError(authErrorMessage(cause)));
  };

  if (restoring) {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-950">
        <Spinner size={22} className="text-chalk-faint" />
      </div>
    );
  }

  return (
    <AuthFrame
      title="Sign in"
      subtitle="Pick up where you left off."
      footer={
        <>
          New here?{' '}
          <AppLink to="/signup" className="text-signal hover:underline">
            Create an account
          </AppLink>
          .
        </>
      }
    >
      <GoogleButton label="Continue with Google" onError={setError} />
      <Divider />

      <form onSubmit={submit} className="space-y-4">
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />

        {error ? <Banner tone="danger">{error}</Banner> : null}
        {notice ? <Banner tone="info">{notice}</Banner> : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          busy={busy}
          disabled={!firebaseConfigured}
        >
          Sign in
        </Button>
      </form>

      <button
        type="button"
        onClick={reset}
        className="text-caption text-chalk-faint underline-offset-2 hover:text-chalk-dim hover:underline"
      >
        Forgot your password?
      </button>
    </AuthFrame>
  );
}

export function SignupPage() {
  const { signUpWithEmail } = useAuth();
  const restoring = useRedirectWhenSignedIn();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Passwords need to be at least six characters.');
      return;
    }

    setBusy(true);
    signUpWithEmail(name, email, password)
      .catch((cause: unknown) => setError(authErrorMessage(cause)))
      .finally(() => setBusy(false));
  };

  if (restoring) {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-950">
        <Spinner size={22} className="text-chalk-faint" />
      </div>
    );
  }

  return (
    <AuthFrame
      title="Create your account"
      subtitle="Free forever for individuals. No card, no trial clock."
      footer={
        <>
          Already have an account?{' '}
          <AppLink to="/login" className="text-signal hover:underline">
            Sign in
          </AppLink>
          .
        </>
      }
    >
      <GoogleButton label="Continue with Google" onError={setError} />
      <Divider />

      <form onSubmit={submit} className="space-y-4">
        <Input
          label="Your name"
          autoComplete="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint="Shown on the videos you share."
          required
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint="At least six characters."
          required
        />

        {error ? <Banner tone="danger">{error}</Banner> : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          busy={busy}
          disabled={!firebaseConfigured}
        >
          Create account
        </Button>
      </form>
    </AuthFrame>
  );
}
